// Cache Service for Silk Road Terminal
// Provides localStorage caching with TTL and stale-while-revalidate pattern

const CACHE_PREFIX = 'sr2_cache_';
const MAX_CACHE_SIZE = 5 * 1024 * 1024; // 5MB
const DEFAULT_TTL = 60; // 60 minutes for economic data
const FX_TTL = 5; // 5 minutes for FX rates

interface CacheEntry<T> {
  data: T;
  timestamp: number;
  ttl: number; // in minutes
  key: string;
}

interface CacheStats {
  totalEntries: number;
  totalSize: number;
  oldestEntry: Date | null;
  newestEntry: Date | null;
}

class CacheService {
  private memoryCache: Map<string, CacheEntry<unknown>> = new Map();

  // Generate cache key from endpoint and params
  private generateKey(endpoint: string, params?: Record<string, string | number>): string {
    const paramString = params
      ? Object.entries(params)
          .sort(([a], [b]) => a.localeCompare(b))
          .map(([k, v]) => `${k}=${v}`)
          .join('_')
      : '';
    return `${CACHE_PREFIX}${endpoint}${paramString ? '_' + paramString : ''}`;
  }

  // Get item from cache
  get<T>(key: string): T | null;
  get<T>(endpoint: string, params?: Record<string, string | number>): T | null;
  get<T>(endpointOrKey: string, params?: Record<string, string | number>): T | null {
    const key = params ? this.generateKey(endpointOrKey, params) : endpointOrKey;

    // Try memory cache first
    const memoryEntry = this.memoryCache.get(key) as CacheEntry<T> | undefined;
    if (memoryEntry && this.isEntryValid(memoryEntry)) {
      return memoryEntry.data;
    }

    // Try localStorage
    try {
      const stored = localStorage.getItem(key);
      if (!stored) return null;

      const entry: CacheEntry<T> = JSON.parse(stored);
      if (this.isEntryValid(entry)) {
        // Also store in memory cache for faster access
        this.memoryCache.set(key, entry);
        return entry.data;
      }

      // Entry expired, remove it
      this.remove(key);
      return null;
    } catch (error) {
      console.warn('Cache read error:', error);
      return null;
    }
  }

  // Get item even if stale (for stale-while-revalidate pattern)
  getStale<T>(endpoint: string, params?: Record<string, string | number>): { data: T | null; isStale: boolean } {
    const key = this.generateKey(endpoint, params);

    // Try memory cache first
    const memoryEntry = this.memoryCache.get(key) as CacheEntry<T> | undefined;
    if (memoryEntry) {
      return {
        data: memoryEntry.data,
        isStale: !this.isEntryValid(memoryEntry),
      };
    }

    // Try localStorage
    try {
      const stored = localStorage.getItem(key);
      if (!stored) return { data: null, isStale: true };

      const entry: CacheEntry<T> = JSON.parse(stored);
      this.memoryCache.set(key, entry);

      return {
        data: entry.data,
        isStale: !this.isEntryValid(entry),
      };
    } catch (error) {
      console.warn('Cache read error:', error);
      return { data: null, isStale: true };
    }
  }

  // Set item in cache
  set<T>(key: string, data: T, ttlMinutes?: number): void;
  set<T>(endpoint: string, params: Record<string, string | number>, data: T, ttlMinutes?: number): void;
  set<T>(
    endpointOrKey: string,
    paramsOrData: Record<string, string | number> | T,
    dataOrTtl?: T | number,
    ttlMinutes?: number
  ): void {
    let key: string;
    let data: T;
    let ttl: number;

    if (typeof dataOrTtl === 'undefined' || typeof dataOrTtl === 'number') {
      // Called as set(key, data, ttl?)
      key = endpointOrKey;
      data = paramsOrData as T;
      ttl = (dataOrTtl as number) ?? DEFAULT_TTL;
    } else {
      // Called as set(endpoint, params, data, ttl?)
      key = this.generateKey(endpointOrKey, paramsOrData as Record<string, string | number>);
      data = dataOrTtl;
      ttl = ttlMinutes ?? DEFAULT_TTL;
    }

    const entry: CacheEntry<T> = {
      data,
      timestamp: Date.now(),
      ttl,
      key,
    };

    // Store in memory cache
    this.memoryCache.set(key, entry);

    // Store in localStorage
    try {
      // Check if we need to free up space
      this.ensureSpace(JSON.stringify(entry).length);
      localStorage.setItem(key, JSON.stringify(entry));
    } catch (error) {
      console.warn('Cache write error:', error);
      // If quota exceeded, clear old entries and try again
      if (error instanceof DOMException && error.name === 'QuotaExceededError') {
        this.clearExpired();
        try {
          localStorage.setItem(key, JSON.stringify(entry));
        } catch {
          console.error('Cache write failed after cleanup');
        }
      }
    }
  }

  // Check if cache entry is valid (not expired)
  isValid(endpoint: string, params?: Record<string, string | number>): boolean {
    const key = this.generateKey(endpoint, params);

    // Check memory cache
    const memoryEntry = this.memoryCache.get(key);
    if (memoryEntry) {
      return this.isEntryValid(memoryEntry);
    }

    // Check localStorage
    try {
      const stored = localStorage.getItem(key);
      if (!stored) return false;

      const entry: CacheEntry<unknown> = JSON.parse(stored);
      return this.isEntryValid(entry);
    } catch {
      return false;
    }
  }

  private isEntryValid(entry: CacheEntry<unknown>): boolean {
    const now = Date.now();
    const expiryTime = entry.timestamp + entry.ttl * 60 * 1000;
    return now < expiryTime;
  }

  // Get time until expiry in minutes
  getTimeToExpiry(endpoint: string, params?: Record<string, string | number>): number | null {
    const key = this.generateKey(endpoint, params);

    try {
      const stored = localStorage.getItem(key);
      if (!stored) return null;

      const entry: CacheEntry<unknown> = JSON.parse(stored);
      const expiryTime = entry.timestamp + entry.ttl * 60 * 1000;
      const remaining = expiryTime - Date.now();

      return remaining > 0 ? Math.ceil(remaining / 60000) : 0;
    } catch {
      return null;
    }
  }

  // Remove item from cache
  remove(key: string): void;
  remove(endpoint: string, params?: Record<string, string | number>): void;
  remove(endpointOrKey: string, params?: Record<string, string | number>): void {
    const key = params ? this.generateKey(endpointOrKey, params) : endpointOrKey;
    this.memoryCache.delete(key);
    try {
      localStorage.removeItem(key);
    } catch (error) {
      console.warn('Cache remove error:', error);
    }
  }

  // Clear all cache entries
  clear(): void {
    this.memoryCache.clear();

    try {
      const keys = Object.keys(localStorage).filter(key => key.startsWith(CACHE_PREFIX));
      keys.forEach(key => localStorage.removeItem(key));
    } catch (error) {
      console.warn('Cache clear error:', error);
    }
  }

  // Clear only expired entries
  clearExpired(): number {
    let clearedCount = 0;

    // Clear from memory cache
    this.memoryCache.forEach((entry, key) => {
      if (!this.isEntryValid(entry)) {
        this.memoryCache.delete(key);
        clearedCount++;
      }
    });

    // Clear from localStorage
    try {
      const keys = Object.keys(localStorage).filter(key => key.startsWith(CACHE_PREFIX));

      keys.forEach(key => {
        try {
          const stored = localStorage.getItem(key);
          if (stored) {
            const entry: CacheEntry<unknown> = JSON.parse(stored);
            if (!this.isEntryValid(entry)) {
              localStorage.removeItem(key);
              clearedCount++;
            }
          }
        } catch {
          // Invalid entry, remove it
          localStorage.removeItem(key);
          clearedCount++;
        }
      });
    } catch (error) {
      console.warn('Cache clearExpired error:', error);
    }

    return clearedCount;
  }

  // Ensure there's enough space for a new entry
  private ensureSpace(requiredBytes: number): void {
    try {
      const currentSize = this.getCacheSize();

      if (currentSize + requiredBytes > MAX_CACHE_SIZE) {
        // Get all entries sorted by timestamp (oldest first)
        const entries: Array<{ key: string; entry: CacheEntry<unknown> }> = [];

        const keys = Object.keys(localStorage).filter(key => key.startsWith(CACHE_PREFIX));
        keys.forEach(key => {
          try {
            const stored = localStorage.getItem(key);
            if (stored) {
              entries.push({ key, entry: JSON.parse(stored) });
            }
          } catch {
            // Invalid entry, remove it
            localStorage.removeItem(key);
          }
        });

        entries.sort((a, b) => a.entry.timestamp - b.entry.timestamp);

        // Remove oldest entries until we have enough space
        let freedSpace = 0;
        const targetFreeSpace = requiredBytes + MAX_CACHE_SIZE * 0.1; // Free 10% extra

        for (const { key } of entries) {
          if (freedSpace >= targetFreeSpace) break;

          const stored = localStorage.getItem(key);
          if (stored) {
            freedSpace += stored.length * 2; // UTF-16 characters are 2 bytes
            localStorage.removeItem(key);
            this.memoryCache.delete(key);
          }
        }
      }
    } catch (error) {
      console.warn('Cache ensureSpace error:', error);
    }
  }

  // Get total cache size in bytes
  private getCacheSize(): number {
    let totalSize = 0;

    try {
      const keys = Object.keys(localStorage).filter(key => key.startsWith(CACHE_PREFIX));
      keys.forEach(key => {
        const item = localStorage.getItem(key);
        if (item) {
          totalSize += item.length * 2; // UTF-16 characters are 2 bytes
        }
      });
    } catch (error) {
      console.warn('Cache getCacheSize error:', error);
    }

    return totalSize;
  }

  // Get cache statistics
  getStats(): CacheStats {
    let oldestTimestamp: number | null = null;
    let newestTimestamp: number | null = null;
    let totalEntries = 0;

    try {
      const keys = Object.keys(localStorage).filter(key => key.startsWith(CACHE_PREFIX));
      totalEntries = keys.length;

      keys.forEach(key => {
        try {
          const stored = localStorage.getItem(key);
          if (stored) {
            const entry: CacheEntry<unknown> = JSON.parse(stored);
            if (oldestTimestamp === null || entry.timestamp < oldestTimestamp) {
              oldestTimestamp = entry.timestamp;
            }
            if (newestTimestamp === null || entry.timestamp > newestTimestamp) {
              newestTimestamp = entry.timestamp;
            }
          }
        } catch {
          // Skip invalid entries
        }
      });
    } catch (error) {
      console.warn('Cache getStats error:', error);
    }

    return {
      totalEntries,
      totalSize: this.getCacheSize(),
      oldestEntry: oldestTimestamp ? new Date(oldestTimestamp) : null,
      newestEntry: newestTimestamp ? new Date(newestTimestamp) : null,
    };
  }
}

// Export singleton instance
export const cacheService = new CacheService();

// Export TTL constants
export { DEFAULT_TTL, FX_TTL };

export default cacheService;
